import { Conversation } from '@nextforce/api'
import { t } from 'app/i18l'
import { useNonDeletedMessages } from 'chat/messages/queries/list'
import { isConversationHasNewMessage, isMessageUnread } from 'chat/shared/util'
import { BaseHomeItem, HomeItem, SummaryItem } from 'home/types'
import {
  makeErrorHomeItem,
  makeSuccessHomeItem,
} from 'home/update_app/shared/utils'
import { useUser } from 'users/queries/detail'
import { useConversations } from './queries/list'
import { getConversationUnread } from './utils'

export function useConversationsHomeItem(): HomeItem | undefined {
  type ConversationMap = {
    [conversationId: string]: Conversation
  }
  const {
    data: conversations,
    isError: isConversationsError,
    error: conversationsError,
  } = useConversations()
  const { data: messages } = useNonDeletedMessages(data => data.messages)
  const { data: user } = useUser()
  const enabled = user?.permissions?.canSeeMessages
  if (!enabled) {
    return
  }

  const canSeeAnnouncements = user?.permissions?.canSeeAnnouncements
  const baseItem = {
    title: canSeeAnnouncements ? t('announcements') : t('messages'),
    route: { name: 'conversations' },
    color: 'black',
    params: {
      screen:
        user?.role === 'observer' || user?.role === 'admin'
          ? 'announcements'
          : 'allConversations',
    },
    backgroundColor: '#FBE2B9',
  } as BaseHomeItem

  if (isConversationsError) {
    return makeErrorHomeItem(baseItem, conversationsError)
  }

  const conversationsMap: ConversationMap =
    conversations
      ?.filter(conversation =>
        canSeeAnnouncements
          ? isAnnouncement(conversation)
          : isConversationHasNewMessage(conversation)
      )
      .reduce<ConversationMap>(
        (map, conversation) => ({ ...map, [conversation.id]: conversation }),
        {}
      ) ?? {}

  const summaryItems: SummaryItem[] =
    messages
      ?.filter(
        m =>
          conversationsMap[m.conversationId] &&
          isMessageUnread(m, conversationsMap[m.conversationId], user)
      )
      .sort((a, b) => b.createdAt - a.createdAt)
      .map(message => ({
        title: conversationsMap[message.conversationId].name,
        description: message.body,
      })) ?? []

  const badgeCount = Object.values(conversationsMap).filter(conversation =>
    getConversationUnread(conversation, user)
  ).length

  return makeSuccessHomeItem(baseItem, summaryItems, badgeCount)
}

function isAnnouncement(conversation: Conversation) {
  return (
    conversation.type === 'companyAnnouncements' ||
    conversation.type === 'nextAnnouncements' ||
    conversation.type === 'nextManagementAnnouncements'
  )
}
